import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  ReactNode,
  MutableRefObject
} from 'react';
import { useLocation } from 'react-router-dom';

export interface PersistedSearch {
  pathname: string;
  value: string;
}

const emptyStore: MutableRefObject<PersistedSearch> = {
  current: { pathname: '', value: '' }
};

const SearchPersistenceContext =
  createContext<MutableRefObject<PersistedSearch>>(emptyStore);

export const SearchPersistenceProvider = ({
  children
}: {
  children: ReactNode;
}) => {
  const store = useRef<PersistedSearch>({ pathname: '', value: '' });
  const { pathname } = useLocation();

  // Clear on real navigation so a search persists only across region switches
  // (which keep the same pathname), never resurrecting on plain navigation.
  useEffect(() => {
    store.current = { pathname: '', value: '' };
  }, [pathname]);

  return (
    <SearchPersistenceContext.Provider value={store}>
      {children}
    </SearchPersistenceContext.Provider>
  );
};

/**
 * Returns the search value to re-apply for the current page, or '' when the
 * persisted value belongs to a different page. Search is carried only within
 * the same pathname, so navigating / collapsing to another page clears it.
 */
export const resolvePersistedSearch = (
  store: PersistedSearch,
  pathname: string
): string => (store.pathname === pathname ? store.value : '');

/**
 * Persists a page's search value across DR-region switches. Region switching
 * remounts the routed page (Layout renders `<Routes key={region.code} />`),
 * which would otherwise wipe the search held in component state. On remount
 * this re-applies the last value for the SAME page via the page's own search
 * handler, so the applied state (fetch, pagination reset) is reproduced.
 *
 * @param currentSearch the page's live search string.
 * @param applySearch the page's search-change handler (the same one the search
 * input's onChange calls), invoked with the persisted value.
 */
export const useRegionPersistedSearch = (
  currentSearch: string,
  applySearch: (value: string) => void
): void => {
  const store = useContext(SearchPersistenceContext);
  const { pathname } = useLocation();

  // Capture the previous instance's value before the sync effect overwrites it.
  const initialSearch = useRef(resolvePersistedSearch(store.current, pathname));
  const currentSearchRef = useRef(currentSearch);
  currentSearchRef.current = currentSearch;
  const applySearchRef = useRef(applySearch);
  applySearchRef.current = applySearch;

  // Keep the store in sync with the live search value for this page.
  useEffect(() => {
    store.current = { pathname, value: currentSearchRef.current };
  }, [store, pathname, currentSearch]);

  // On a region-switch remount, re-apply the persisted value for the same page.
  // Deferred to a macrotask so it runs after the page's own URL hydration.
  useEffect(() => {
    const value = initialSearch.current;
    if (!value) return undefined;
    const timer = setTimeout(() => {
      if (value !== currentSearchRef.current) {
        applySearchRef.current(value);
      }
    }, 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
